import { SearchFilters, SearchResultsTab } from '../types/search';
import { safeSessionStorage } from './safeStorage';

type SearchStateInstance = {
  scrollPosition: number;
  numberOfItems: number;
};
const STORAGE_KEY = 'lastSearchState';

export type ResultSet = {
  mindItems?: SearchStateInstance;
  products?: SearchStateInstance;
  videoMindItems?: SearchStateInstance;
  courseMindItems?: SearchStateInstance;
  all?: SearchStateInstance;
};
type SearchState = ResultSet & {
  filters?: SearchFilters,
  courseFilters?: SearchFilters,
  tabKey?: SearchResultsTab,
  searchId?: string,
  searchSuggestionApplied?: boolean,
  relatedTopicsApplied?: boolean,
};

const getSearchState: () => SearchState = () => {
  const searchState = safeSessionStorage.getItem(STORAGE_KEY);
  return searchState ? JSON.parse(searchState) : {};
};

const setSearchState = (searchState: SearchState) => {
  safeSessionStorage.setItem(STORAGE_KEY, JSON.stringify(searchState));
};

const getSearchFilters = () => {
  const searchState = getSearchState();
  return searchState.filters;
};

const getCourseSearchFilters = () => {
  const searchState = getSearchState();
  return searchState.courseFilters;
};

const getSearchId = () => {
  const searchState = getSearchState();
  return searchState.searchId;
};

const setSearchId = (searchId: string) => {
  const searchState = getSearchState();
  searchState.searchId = searchId;
  setSearchState(searchState);
};

const getSearchSuggestionApplied = () => {
  const searchState = getSearchState();
  return searchState.searchSuggestionApplied;
};

const setSearchSuggestionApplied = (value: boolean | undefined) => {
  const searchState = getSearchState();
  if (value === undefined) {
    delete searchState.searchSuggestionApplied;
  } else {
    searchState.searchSuggestionApplied = value;
  }
  setSearchState(searchState);
};

const getRelatedTopicsApplied = () => {
  const searchState = getSearchState();
  return searchState.relatedTopicsApplied;
};

const setRelatedTopicsApplied = (value: boolean | undefined) => {
  const searchState = getSearchState();
  if (value === undefined) {
    delete searchState.relatedTopicsApplied;
  } else {
    searchState.relatedTopicsApplied = value;
  }
  setSearchState(searchState);
};

const setSearchFilters = (filters: SearchFilters) => {
  const searchState = getSearchState();
  searchState.filters = filters;
  setSearchState(searchState);
};

const setCourseSearchFilters = (courseFilters: SearchFilters) => {
  const searchState = getSearchState();
  searchState.courseFilters = courseFilters;
  setSearchState(searchState);
};

const clearSearchState = (preserveJourneyFlags = false) => {
  const searchId = getSearchId();
  const tabKey = getSearchTabKey();
  const searchSuggestionApplied = preserveJourneyFlags ? getSearchSuggestionApplied() : undefined;
  const relatedTopicsApplied = preserveJourneyFlags ? getRelatedTopicsApplied() : undefined;
  safeSessionStorage.removeItem(STORAGE_KEY);
  if (searchId) {
    // Preserve the active search journey ID when wiping session-scoped search state
    // so refinements (filters) keep using the same search_id until a new search starts.
    setSearchId(searchId);
  }
  if (tabKey) {
    // Preserve the active tab so breadcrumbs on product/video/reader pages link back to the correct search tab.
    setSearchTabKey(tabKey);
  }
  // Only the filter-reset path preserves journey-scoped flags so the downstream
  // SearchCompleted event still sees them. Reload/navigation must wipe them to
  // avoid misattributing later, unrelated searches.
  if (searchSuggestionApplied !== undefined) {
    setSearchSuggestionApplied(searchSuggestionApplied);
  }
  if (relatedTopicsApplied !== undefined) {
    setRelatedTopicsApplied(relatedTopicsApplied);
  }
};

const setSearchStateInstance = (keyofSearchState: keyof ResultSet, numberOfItems: number, scrollPosition: number) => {
  const searchState = getSearchState();
  searchState[keyofSearchState] = { numberOfItems, scrollPosition };
  setSearchState(searchState);
};

const getSearchStateInstance = (keyofSearchState: keyof ResultSet) => {
  const searchState = getSearchState();
  return searchState[keyofSearchState];
};

const getSearchTabKey = (): SearchResultsTab | undefined => {
  const searchState = getSearchState();
  return searchState.tabKey;
};

const setSearchTabKey = (tabKey: SearchResultsTab) => {
  const searchState = getSearchState();
  searchState.tabKey = tabKey;
  setSearchState(searchState);
};

const searchResultsStorageManager = {
  getSearchState,
  setSearchState,
  getSearchFilters,
  getCourseSearchFilters,
  setSearchFilters,
  setCourseSearchFilters,
  clearSearchState,
  getSearchStateInstance,
  setSearchStateInstance,
  getSearchTabKey,
  setSearchTabKey,
  getSearchId,
  setSearchId,
  getSearchSuggestionApplied,
  setSearchSuggestionApplied,
  getRelatedTopicsApplied,
  setRelatedTopicsApplied,
};

export default searchResultsStorageManager;
